import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import Chip from '@/components/ui/Chip';
import { formatarReais, formatarData, pad4 } from '@/pages/talao/utils';
import type { RifaMinha } from '../types';

type ChipTipo = 'success' | 'warning' | 'danger' | 'secondary' | 'info' | 'neutro';

// Mapeia o status da rifa para o chip do cartão.
function chipDoStatus(status: string): { tipo: ChipTipo; rotulo: string } {
  switch (status) {
    case 'publicada':
      return { tipo: 'success', rotulo: 'Vendendo' };
    case 'sorteada':
    case 'encerrada':
      return { tipo: 'info', rotulo: 'Apurada' };
    case 'cancelada':
      return { tipo: 'neutro', rotulo: 'Cancelada' };
    default:
      return { tipo: 'neutro', rotulo: status };
  }
}

interface Props {
  rifa: RifaMinha;
  imagem: string | null;
}

export default function CartaoRifa({ rifa, imagem }: Props) {
  const navigate = useNavigate();
  const [pegando, setPegando] = useState(false);
  const [erroPegar, setErroPegar] = useState<string | null>(null);
  const [avisoPegar, setAvisoPegar] = useState<string | null>(null);

  const temTalao = rifa.talao_id != null;
  const percentual = rifa.tenho > 0 ? Math.min(100, Math.round((rifa.vendi / rifa.tenho) * 100)) : 0;
  const chip = chipDoStatus(rifa.status);

  async function pegarTalao() {
    setPegando(true);
    setErroPegar(null);
    setAvisoPegar(null);
    try {
      const { data, error } = await supabase.rpc('pegar_talao', { p_rifa: rifa.rifa_id });
      if (error) throw error;
      const linhas = (data as unknown[]) || [];
      if (linhas.length === 0) {
        setErroPegar('Não foi possível pegar um talão agora. Tente de novo.');
        return;
      }
      const novo = linhas[0] as { talao_id: number | null; situacao: string };
      if (novo.talao_id == null) {
        setAvisoPegar(novo.situacao);
        return;
      }
      navigate(`/minhas-rifas/${rifa.rifa_id}/capa`);
    } catch (e) {
      setErroPegar(mensagemAmigavel(e, 'Não foi possível pegar o talão.'));
    } finally {
      setPegando(false);
    }
  }

  return (
    <div className="overflow-hidden rounded-[22px] border border-secondary-100 bg-white">
      {/* Capa desfocada */}
      {imagem ? (
        <div className="relative h-24 w-full overflow-hidden">
          <img
            src={imagem}
            alt=""
            aria-hidden
            className="h-full w-full object-cover object-center"
            style={{ filter: 'blur(8px)', transform: 'scale(1.1)' }}
            loading="lazy"
            decoding="async"
          />
          <div className="absolute inset-0 bg-black/20" />
        </div>
      ) : (
        <div className="h-24 w-full bg-gradient-to-br from-primary-500 to-primary-400" />
      )}

      <div className="p-5">
        {/* Título + chip + data */}
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-[17px] font-bold leading-snug text-foreground-950">{rifa.titulo}</h2>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <Chip tipo={chip.tipo}>{chip.rotulo}</Chip>
          {rifa.sorteio_em && (
            <span className="text-sm text-secondary-500">
              Resultado em {formatarData(rifa.sorteio_em)}
            </span>
          )}
        </div>

        {/* Placar + barra */}
        {temTalao && (
          <div className="mt-4">
            <div className="flex items-baseline justify-between">
              <p className="text-[28px] font-extrabold leading-none text-foreground-950 md:text-[36px]">
                {rifa.vendi}{' '}
                <span className="text-base font-semibold text-secondary-500 md:text-lg">de {rifa.tenho}</span>
              </p>
              <p className="text-sm font-semibold text-secondary-500">vendidos</p>
            </div>
            <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-secondary-100">
              <div
                className="h-full rounded-full bg-success-500 transition-all duration-500"
                style={{ width: `${percentual}%` }}
              />
            </div>
          </div>
        )}

        {/* Mini-tiles */}
        <div className={`mt-4 grid gap-2 ${rifa.abate_inscricao ? 'grid-cols-2' : 'grid-cols-1'}`}>
          <div className="rounded-2xl bg-background-50 px-4 py-3">
            <p className="text-lg font-bold text-foreground-950 md:text-[28px] md:font-extrabold">{formatarReais(rifa.arrecadei_reais)}</p>
            <p className="mt-0.5 text-xs text-secondary-500">você vendeu</p>
          </div>
          {rifa.abate_inscricao && (
            <div className="rounded-2xl bg-background-50 px-4 py-3">
              <p className="text-lg font-bold text-foreground-950 md:text-[28px] md:font-extrabold">{formatarReais(rifa.ainda_falta_reais)}</p>
              <p className="mt-0.5 text-xs text-secondary-500">faltam na sua inscrição</p>
            </div>
          )}
        </div>

        {rifa.dinheiro_na_mao_reais > 0 && (
          <p className="mt-3 rounded-xl bg-warning-50 px-3 py-2 text-sm text-warning-800">
            {formatarReais(rifa.dinheiro_na_mao_reais)} em dinheiro com você — entregue para a
            organização
          </p>
        )}

        {/* Botão de ação */}
        {temTalao ? (
          <Link
            to={`/minhas-rifas/${rifa.rifa_id}/capa`}
            className="mt-4 flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-2xl bg-primary-500 px-6 py-3.5 text-sm font-bold text-white transition-colors hover:bg-primary-600"
          >
            Vender agora
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-arrow-right-line" />
            </span>
          </Link>
        ) : (
          <>
            <button
              type="button"
              onClick={pegarTalao}
              disabled={pegando}
              className="mt-4 flex w-full cursor-pointer items-center justify-center whitespace-nowrap rounded-2xl bg-primary-500 px-6 py-3.5 text-sm font-bold text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {pegando ? 'Pegando…' : 'Pegar talão'}
            </button>
            {avisoPegar && <p className="mt-2 text-sm text-secondary-600">{avisoPegar}</p>}
            {erroPegar && <p className="mt-2 text-sm text-danger-600">{erroPegar}</p>}
          </>
        )}

        {temTalao && (
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
            {rifa.inicio != null && rifa.fim != null ? (
              <p className="text-[13px] text-secondary-500">
                Meus números: {pad4(rifa.inicio)} a {pad4(rifa.fim)}
              </p>
            ) : (
              <span />
            )}
            <Link
              to={`/meu-talao/${rifa.rifa_id}?cartela=1`}
              className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap text-[13px] font-semibold text-primary-600 transition-colors hover:text-primary-700"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-download-2-line" />
              </span>
              Baixar minha cartela
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}